import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ExistingCapabilityDialog({ open, onClose, officers }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'acquired',
    system_tech_component: '',
    people_component: '',
    process_component: '',
    sponsor_email: '',
    actual_completion_date: new Date().toISOString().split('T')[0]
  });

  const addExistingCapabilityMutation = useMutation({
    mutationFn: async (data) => {
      const user = await base44.auth.me();
      return base44.entities.Capability.create({
        ...data,
        requested_by: user.email,
        requested_at: new Date().toISOString(),
        status: 'active'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['capabilities'] });
      onClose();
      setFormData({
        name: '',
        description: '',
        type: 'acquired',
        system_tech_component: '',
        people_component: '',
        process_component: '',
        sponsor_email: '',
        actual_completion_date: new Date().toISOString().split('T')[0]
      });
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    addExistingCapabilityMutation.mutate(formData);
  };

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="issues-existingcapabilitydialog-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Existing Capability</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Capability Name *</Label>
            <Input data-testid="issues-existingcapabilitydialog-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-slate-950 border-slate-800"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea data-testid="issues-existingcapabilitydialog-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-slate-950 border-slate-800"
              rows={3}
              placeholder="What does this capability enable?"
            />
          </div>

          <div className="space-y-2">
            <Label>Type *</Label>
            <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
              <SelectTrigger className="bg-slate-950 border-slate-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contracted">Contracted (External)</SelectItem>
                <SelectItem value="acquired">Acquired (Purchase/License)</SelectItem>
                <SelectItem value="developed">Developed (Build In-House)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4 p-4 bg-slate-950/50 border border-slate-800 rounded-lg">
            <h3 className="text-sm font-semibold text-slate-300">Capability Components</h3>
            
            <div className="space-y-2">
              <Label>System/Technology Component</Label>
              <Textarea data-testid="issues-existingcapabilitydialog-textarea-1"
                value={formData.system_tech_component}
                onChange={(e) => setFormData({ ...formData, system_tech_component: e.target.value })}
                className="bg-slate-950 border-slate-800"
                rows={2}
                placeholder="What systems, tools, or technology?"
              />
            </div>

            <div className="space-y-2">
              <Label>People Component</Label>
              <Textarea data-testid="issues-existingcapabilitydialog-textarea-2"
                value={formData.people_component}
                onChange={(e) => setFormData({ ...formData, people_component: e.target.value })}
                className="bg-slate-950 border-slate-800"
                rows={2}
                placeholder="What skills, roles, or training?"
              />
            </div>

            <div className="space-y-2">
              <Label>Process Component</Label>
              <Textarea data-testid="issues-existingcapabilitydialog-textarea-3"
                value={formData.process_component}
                onChange={(e) => setFormData({ ...formData, process_component: e.target.value })}
                className="bg-slate-950 border-slate-800"
                rows={2}
                placeholder="What processes or workflows?"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Sponsor</Label>
              <Select value={formData.sponsor_email} onValueChange={(value) => setFormData({ ...formData, sponsor_email: value })}>
                <SelectTrigger className="bg-slate-950 border-slate-800">
                  <SelectValue placeholder="Select sponsor..." />
                </SelectTrigger>
                <SelectContent>
                  {officers.map((officer) => (
                    <SelectItem key={officer.id} value={officer.user_email}>
                      {officer.callsign}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Completion Date</Label>
              <Input data-testid="issues-existingcapabilitydialog-input-1"
                type="date"
                value={formData.actual_completion_date}
                onChange={(e) => setFormData({ ...formData, actual_completion_date: e.target.value })}
                className="bg-slate-950 border-slate-800"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button data-testid="issues-cancel-btn" type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
              Add Capability
            </Button>
          </div>
        </form>
      </DialogContent>
    </ConfirmableDialog>
  );
}